import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import { RootState } from "../../app/store";
import { getDistanceBetweenPoints } from "../../geometry";
import { parseDirectTelemetry } from "../../tools/parse";
import {
  selectSelectedUavId,
  selectDirectById,
  selectGeofenceIdByUav,
} from "../aircraft/aircraftSelectors";

import {
  Cassette,
  errorHasMannaMessage,
  errorHasMessage,
  isVerboseTelemetry,
  Mission,
  Route,
  UavRoute,
} from "../aircraft/types";
import {
  selectLocationData,
  selectUsername,
} from "../session/sessionSlice";
import {
  generateMission,
  getCassettes,
  getRoutes,
  sendGcsReport,
  sendMissionReport,
} from "./missionAPI";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { Logger } from "../../tools/Logger";
import {
  issueCommandThunk,
  onEvent,
  resetConnections,
} from "../aircraft/aircraftSlice";
import { selectGeofences } from "../airspace/airspaceSlice";

export type Locations = "none" | "moorock" | "balbriggan" | "kerry";
const TAG = "missionSlice";
export const MAX_DISPLAY_DISTANCE_METERS = 20000;

interface DeliveryFeedback {
  isPositive: boolean;
  feedbackType: string;
  otherFeedback: string;
  feedbackText: string;
  issueLevel?: "NO_ISSUE" | "MINOR_ISSUE" | "MAJOR_ISSUE";
}

export interface MissionState {
  missionSelect: boolean;
  routes: Route[];
  cassettes: Cassette[];
  uavRoutes: { [key: string]: UavRoute };
  uavMissions: { [key: string]: Mission };
  uavCassettes: { [key: string]: Cassette };
  deliveryFeedback: { [key: string]: DeliveryFeedback };
}

const initialState: MissionState = {
  missionSelect: false,
  routes: [],
  cassettes: [],
  uavRoutes: {},
  uavMissions: {},
  uavCassettes: {},
  deliveryFeedback: {},
};

export const getRoutesThunk = createAsyncThunk<
  Route[],
  undefined,
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("mission/getRoute", async (ignore, thunkApi) => {
  try {
    const res = await getRoutes();
    const locationData = selectLocationData(thunkApi.getState());

    const routes = res?.filter?.((route) => {
      if (!route.enabled) return false;
      const distanceFromTablet = getDistanceBetweenPoints(
        [route?.address_latitude, route?.address_longitude],
        locationData.latLon
      );

      const display = distanceFromTablet < MAX_DISPLAY_DISTANCE_METERS;

      return display;
    });

    return thunkApi.fulfillWithValue(routes, {
      message: "Routes retrieved!",
    });
  } catch (err) {
    Logger.error(TAG, JSON.stringify(err));

    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: "[GET] routes failed: " + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: "[GET] routes failed",
    });
  }
});
interface MissionReportAction {
  missionId: string;
  issueLevel: "NO_ISSUE" | "MINOR_ISSUE" | "MAJOR_ISSUE";
  description: string;
  uavId: string;
  cassetteId?: string;
}

export const missionReportThunk = createAsyncThunk<
  {},
  MissionReportAction,
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("mission/report", async (payload, thunkApi) => {
  try {
    const username = selectUsername(thunkApi.getState());
    const telemetry = selectDirectById(thunkApi.getState(), payload.uavId);
    const geofences = selectGeofences(thunkApi.getState());
    const geofenceId = isVerboseTelemetry(telemetry?.payload) 
      ? telemetry.payload.geofence_id 
      : selectGeofenceIdByUav(thunkApi.getState(), payload.uavId);
    
    if (!geofences || geofences.length === 0) {
      Logger.warn(TAG, "No geofences available for flight type determination");
    }
    if (!geofenceId) {
      Logger.warn(TAG, `No geofence_id available for UAV ${payload.uavId}`);
    }
    
    const geofence = geofences.find(g => g.name === geofenceId);

    const response = await sendMissionReport({
      missionId: payload.missionId,
      flightType: geofence?.type === "TEST" ? "VOLUME_TESTING" : "PRODUCTION",
      username,
      issue: payload.issueLevel,
      description: payload.description,
      gcs: process.env.REACT_APP_BASE_HASH,
    });
    return thunkApi.fulfillWithValue(response, {
      message: "Mission report sent!",
    });
  } catch (error) {
    console.error(error);

    if (errorHasMessage(error)) {
      return thunkApi.rejectWithValue(error, {
        message: "Mission report failed: " + error?.message,
      });
    }
    return thunkApi.rejectWithValue(error, {
      message: "Mission report failed",
    });
  }
});
export const missionGcsThunk = createAsyncThunk<
  {},
  { missionId: string; uavId: string },
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("mission/gcsReport", async (payload, thunkApi) => {
  try {
    const username = selectUsername(thunkApi.getState());
    const telemetry = selectDirectById(thunkApi.getState(), payload.uavId);
    const geofences = selectGeofences(thunkApi.getState());
    const geofenceId = isVerboseTelemetry(telemetry?.payload) 
      ? telemetry.payload.geofence_id 
      : selectGeofenceIdByUav(thunkApi.getState(), payload.uavId);
    
    if (!geofences || geofences.length === 0) {
      Logger.warn(TAG, "No geofences available for flight type determination");
    }
    if (!geofenceId) {
      Logger.warn(TAG, `No geofence_id available for UAV ${payload.uavId}`);
    }
    
    const geofence = geofences.find(g => g.name === geofenceId);
    const flightType = geofence?.type === "TEST" ? "VOLUME_TESTING" : "PRODUCTION";

    const response = await sendGcsReport({
      missionId: payload.missionId,
      flightType,
      issue: "NO_ISSUE",
      username,
      gcs: process.env.REACT_APP_BASE_HASH,
    });
    return thunkApi.fulfillWithValue(response, {});
  } catch (error) {
    console.error(error);

    if (errorHasMessage(error)) {
      return thunkApi.rejectWithValue(error, {
        message: "Mission report failed: " + error?.message,
      });
    }
    return thunkApi.rejectWithValue(error, {
      message: "Mission report failed",
    });
  }
});

export const generateMissionThunk = createAsyncThunk<
  { mission: Mission; uavId: string },
  { orderId: string; uavId: string },
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("mission/generateMission", async ({ orderId, uavId }, thunkApi) => {
  try {
    const telemetry = selectDirectById(thunkApi.getState(), uavId);
    const parsedTelemetry = parseDirectTelemetry(telemetry);
    if (!parsedTelemetry) {
      throw new Error("No telemetry available for UAV position");
    }
    const res = await generateMission(orderId, uavId, parsedTelemetry?.pos);

    const geoJson = JSON.parse(res?.geoJsonLineString);

    return thunkApi.fulfillWithValue(
      { mission: { ...res, geoJson }, uavId },
      {
        message: "Mission generated!",
      }
    );
  } catch (err) {
    const errorString = "[GET] Mission generate failed";
    if (errorHasMannaMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString} with code ${err?.response?.data.errorCode}: ${err?.response?.data.errorMessage}`,
      });
    }
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString}: ` + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: errorString,
    });
  }
});
export const getCassettesThunk = createAsyncThunk<
  Cassette[],
  undefined,
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("mission/getCassettes", async (_, thunkApi) => {
  try {
    const res = await getCassettes();

    return thunkApi.fulfillWithValue(res, {
      message: "Cassettes retrieved",
    });
  } catch (err) {
    const errorString = "[GET] Cassettes failed";
    if (errorHasMannaMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString} with code ${err?.response?.data.errorCode}: ${err?.response?.data.errorMessage}`,
      });
    }
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: `${errorString}: ` + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: errorString,
    });
  }
});

export const missionSlice = createSlice({
  name: "mission",
  initialState,
  reducers: {
    setMissionSelect: (state, action: PayloadAction<boolean>) => {
      state.missionSelect = action.payload;
    },
    setUavMission: (
      state,
      action: PayloadAction<{ uavId: string; mission: Mission }>
    ) => {
      state.uavMissions[action.payload.uavId] = action.payload.mission;
    },
    setUavCassette: (
      state,
      action: PayloadAction<{ uavId: string; cassette: Cassette }>
    ) => {
      state.uavCassettes[action.payload.uavId] = action.payload.cassette;
    },
    setUavRoute: (
      state,
      action: PayloadAction<{ uavId: string; route: Route }>
    ) => {
      state.uavRoutes[action.payload.uavId] = {
        uavId: action.payload.uavId,
        route: action.payload.route,
        mission: null,
        missionLoaded: false,
      };
    },
    restartUavConnection: (state, action: PayloadAction<{ uavId: string }>) => {
      const { [action.payload.uavId]: removeConnections, ...restConnections } =
        state.uavRoutes;
      const { [action.payload.uavId]: removeDirect, ...restDirect } =
        state.uavMissions;
      state.uavRoutes = restConnections;
      state.uavMissions = restDirect;
    },
    saveDeliveryFeedback: (
      state,
      action: PayloadAction<{ uavId: string; feedback: DeliveryFeedback }>
    ) => {
      const { uavId, feedback } = action.payload;
      state.deliveryFeedback[uavId] = feedback;
    },
    clearDeliveryFeedback: (state, action: PayloadAction<string>) => {
      const uavId = action.payload;
      delete state.deliveryFeedback[uavId];
    },
    clearAllDeliveryFeedback: (state) => {
      state.deliveryFeedback = {};
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getRoutesThunk.fulfilled, (state, action) => {
      state.routes = action.payload;
    });
    builder.addCase(resetConnections, (state, action) => {
      state.uavRoutes = {};
      state.uavMissions = {};
    });
    builder.addCase(onEvent, (state, action) => {
      if (
        action.payload.event === "MISSION_STOPPED" ||
        action.payload.event === "MISSION_RESET" ||
        action.payload.event === "VALIDATION_FAILED"
      ) {
        const { [action.payload.uavId]: remove, ...rest } = state.uavMissions;
        state.uavMissions = rest;
        const { [action.payload.uavId]: removeRoute, ...restRoutes } =
          state.uavRoutes;
        state.uavRoutes = restRoutes;
      }
    });
    builder.addCase(generateMissionThunk.pending, (state, action) => {
      const { [action.meta.arg.uavId]: remove, ...rest } = state.uavMissions;

      state.uavMissions = rest;
    });
    builder.addCase(generateMissionThunk.fulfilled, (state, action) => {
      state.uavMissions[action.payload.uavId] = action.payload.mission;
    });
    builder.addCase(getCassettesThunk.fulfilled, (state, action) => {
      state.cassettes = action.payload;
    });
    builder.addCase(generateMissionThunk.rejected, (state, action) => {
      const { [action.meta.arg.uavId]: remove, ...rest } = state.uavRoutes;
      state.uavRoutes = rest;
    });
    builder.addCase(issueCommandThunk.fulfilled, (state, action) => {
      if (action.meta.arg.command === "COMMAND_CANCEL") {
        const { [action.meta.arg.uavId]: remove, ...rest } = state.uavMissions;
        state.uavMissions = rest;
      }
    });
  },
});

export const { 
  setMissionSelect, 
  setUavRoute, 
  setUavMission, 
  setUavCassette,
  saveDeliveryFeedback,
  clearDeliveryFeedback,
  clearAllDeliveryFeedback
} = missionSlice.actions;

export const selectMissionSelect = (state: RootState) =>
  state.mission.missionSelect;

export const selectRoutes = (state: RootState) => state.mission.routes;
export const selectCassettes = (state: RootState) => state.mission.cassettes;

export const selectUavRoutes = (state: RootState) => state.mission.uavRoutes;
export const selectUavMissions = (state: RootState) =>
  state.mission.uavMissions;
export const selectUavCassettes = (state: RootState) =>
  state.mission.uavCassettes;
const selectAircraftId = (_: RootState, uavId: string) => uavId;
export const selectUavRoute = createSelector(
  [selectSelectedUavId, selectUavRoutes],
  (selectedUavId, uavRoutes) => {
    return Object.values(uavRoutes).find((uavRoute) => {
      return uavRoute.uavId === selectedUavId;
    });
  }
);
export const selectMissionById = createSelector(
  selectUavMissions,
  selectAircraftId,
  (missions, id) => {
    return missions[id];
  }
);
export const selectCassetteById = createSelector(
  selectUavCassettes,
  selectAircraftId,
  (cassettes, id) => {
    return cassettes[id];
  }
);
export const selectRouteById = createSelector(
  selectUavRoutes,
  selectAircraftId,
  (missions, id) => {
    return missions[id];
  }
);

export const selectDeliveryFeedback = createSelector(
  (state: RootState) => state.mission.deliveryFeedback,
  selectAircraftId,
  (deliveryFeedback, uavId) => deliveryFeedback[uavId] || null
);

export const selectAllDeliveryFeedback = (state: RootState) =>
  state.mission.deliveryFeedback;

export default persistReducer(
  {
    key: "mission",
    storage,
    whitelist: ["routes", "uavRoutes", "uavMissions", "pads"],
  },
  missionSlice.reducer
);
